/**
 * AI Feature Module
 *
 * Exports all AI-related components, hooks, and services.
 */

import React from 'react';
import ReactDOM from 'react-dom';

// Components
export { AIPromptModal } from './AIPromptModal';
export { AIChatPanel } from './AIChatPanel';
export { AIPreviewPane } from './AIPreviewPane';
export { AIEntryButton, AICardButton } from './AIEntryButton';
export { AIThemePicker } from './AIThemePicker';
export { AICreatorPage } from './AICreatorPage';
export { AIThemePlayer, AIThemeThumbnail } from './AIThemePlayer';
export { EditorAIPanel } from './EditorAIPanel';

// Import components directly for window API
import { AICreatorPage } from './AICreatorPage';
import { AIThemePlayer } from './AIThemePlayer';
import { AIThemePicker } from './AIThemePicker';
import { AIPreviewPane } from './AIPreviewPane';
import { EditorAIPanel } from './EditorAIPanel';

// Context
export { AIProvider, useAI, useAIOptional } from './context/AIContext';

// Hooks
export { useAISession } from './hooks/useAISession';
export { useAIGeneration } from './hooks/useAIGeneration';
export { useAIRecompile } from './hooks/useAIRecompile';

// Services
export { aiService, AIService } from './services/AIService';
export type {
  AIGenerationOptions,
  AIGenerationResult,
  AIRefinementResult,
  AIHistoryResult,
  AIResources,
  AIExportResult,
} from './services/AIService';

// Styles
import './ai.css';

/**
 * Render the AI Creator Page to a DOM element
 * Uses React 16 compatible rendering API
 */
export function renderCreatorPage(container: HTMLElement): void {
  ReactDOM.render(React.createElement(AICreatorPage), container);
}

/**
 * Unmount the AI Creator Page from a DOM element
 */
export function unmountCreatorPage(container: HTMLElement): boolean {
  return ReactDOM.unmountComponentAtNode(container);
}

/**
 * Initialize and attach to window for non-bundled usage
 */
function initializeWindowAPI(): void {
  if (typeof window !== 'undefined') {
    (window as any).FlowVellaAI = {
      renderCreatorPage,
      unmountCreatorPage,
      AICreatorPage,
      AIThemePlayer,
      AIThemePicker,
      AIPreviewPane,
      EditorAIPanel,
    };
  }
}

// Auto-initialize
initializeWindowAPI();
